import * as React from "react";
import { useEffect, useRef } from "react";
import { useBackLayer } from "./backStack.js";

export interface UseDialogFocusOptions {
  /** Whether the dialog is open. The trap and key handling only act while open. */
  open: boolean;
  /** Called when the user presses Escape or a TV remote Back key. */
  onClose: () => void;
  /** Container to trap Tab focus within while open. Defaults to document.body. */
  rootRef?: React.RefObject<HTMLElement | null>;
  /** Move focus into the dialog on open and restore it on close (default true). */
  manageFocus?: boolean;
  /** Wrap Tab focus within rootRef (default true). */
  trapFocus?: boolean;
  /**
   * A layer opened from inside this dialog (a trailer) has Back and the keys
   * for now. Paused, the dialog stops answering them, but stays open as far as
   * focus goes: nothing is restored when it pauses and nothing is moved when it
   * resumes, so the layer above can give focus back to the button that opened
   * it. Closing and reopening instead sent focus to whatever opened the dialog
   * while the trailer played, and to the dialog's first button after it (onn
   * box, owner, 2026-10-07).
   */
  paused?: boolean;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * True for the keys a TV remote Back button can emit across Android TV, Fire
 * TV, and webviews: Escape (desktop/Chromecast), GoBack (Android TV webview),
 * Backspace (classic Android TV Chrome), and keyCode 4 (KEYCODE_BACK).
 */
function isBackKey(event: KeyboardEvent): boolean {
  return event.key === "Escape" || event.key === "GoBack" || event.key === "Backspace" || event.keyCode === 4;
}

/**
 * Focus management + TV-remote Back/Escape handling for modal dialogs.
 *
 * While `open`:
 * - Traps Tab focus inside `rootRef` so the remote's D-pad/Enter never walks
 *   focus behind the dialog.
 * - Closes on Escape or a TV remote Back key, preventing the browser's
 *   default history-back navigation (which a TV webview otherwise triggers).
 * - Moves focus to the first focusable on open and restores the previously
 *   focused element on close — the TV expectation that Back returns you
 *   exactly where you were.
 *
 * Pass `manageFocus: false` / `trapFocus: false` for transient, non-modal
 * overlays (e.g. the mini-EPG toast) that must close on Back but must not
 * steal focus from the guide.
 *
 * It also registers with the back stack, which is what makes a TV REMOTE'S
 * Back close the dialog. The key handling below only ever saw Escape and the
 * webview key names; Android's hardware Back never reaches JavaScript at all —
 * it goes to the activity, which walked history and left the app (owner,
 * 2026-09-22: "sometimes it exits the app"). Every dialog in the app uses this
 * hook, so they all gained Back at once rather than one at a time.
 */
export function useDialogFocus({ open, onClose, rootRef, manageFocus = true, trapFocus = true, paused = false }: UseDialogFocusOptions): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const answering = open && !paused;
  useBackLayer(answering, () => onCloseRef.current());

  // Focus in on open, back out on close. Not tied to `paused` (see it).
  useEffect(() => {
    if (!open || !manageFocus) return;
    // Resolve the root inside the effect, not during render: when a dialog
    // mounts in the same render that flips `open`, rootRef.current is still
    // null during render but is set by the time effects run.
    const root = rootRef?.current ?? document.body;
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = getFocusable(root)[0];
    (first ?? root).focus();
    return () => previouslyFocused.current?.focus();
  }, [open, rootRef, manageFocus]);

  useEffect(() => {
    if (!answering) return;
    const trapRoot = rootRef?.current ?? document.body;

    const onKeyDown = (event: KeyboardEvent) => {
      // Let text fields handle Backspace for deletion themselves.
      const target = event.target as HTMLElement | null;
      if (event.key === "Backspace" && target?.matches?.("input, textarea, select, [contenteditable]")) return;

      if (isBackKey(event)) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (!trapFocus || event.key !== "Tab") return;

      const focusable = getFocusable(trapRoot);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      const atFirstEdge = event.shiftKey && (active === first || !trapRoot.contains(active));
      const atLastEdge = !event.shiftKey && (active === last || !trapRoot.contains(active));
      if (atFirstEdge || atLastEdge) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [answering, rootRef, trapFocus]);
}
